import { useNavigate } from 'react-router-dom';
import { Heart, Loader2, Pencil, Phone, Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SectionHeader } from '@/components/ui/section-header';
import { StatusPill, type StatusTone } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { AuthImage } from '@/components/auth-image';
import { form_error_message } from '@/lib/forms';
import { AdoptTabs } from '@/components/adopt-tabs';
import { use_my_listings, use_listing_mutations, use_listing_requests } from '@/features/adoption-listing/hooks';
import { SPECIES_LABEL, age_label, type AdoptionListing, type AdoptionListingStatus } from '@/features/adoption-listing/types';

const STATUS_TONE: Record<AdoptionListingStatus, StatusTone> = {
  DRAFT: 'warning',
  PUBLISHED: 'success',
  ADOPTED: 'info',
  CLOSED: 'neutral',
  HIDDEN: 'destructive',
  REMOVED: 'destructive',
};

function InterestedAdopters({ listing_id }: { listing_id: string }) {
  const q = use_listing_requests(listing_id);
  const rows = q.data ?? [];
  if (q.isLoading) return <p className="text-xs text-muted-foreground">Loading interest…</p>;
  if (rows.length === 0) return <p className="text-xs text-muted-foreground" data-testid={`interested-empty-${listing_id}`}>No one has paid to reach you yet.</p>;
  return (
    <div className="flex flex-col gap-1.5" data-testid={`interested-${listing_id}`}>
      <p className="text-xs font-semibold text-foreground">{rows.length} interested adopter{rows.length > 1 ? 's' : ''}</p>
      {rows.map((r) => (
        <div key={r.id} className="flex items-center gap-2 rounded-input bg-secondary/40 px-2.5 py-1.5 text-sm">
          <span className="font-medium">{r.adopter.name ?? 'Adopter'}</span>
          {r.adopter.phone && (
            <a href={`tel:${r.adopter.phone}`} className="ml-auto flex items-center gap-1 text-primary hover:underline">
              <Phone className="h-3.5 w-3.5" /> {r.adopter.phone}
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

function ListingRow({ listing }: { listing: AdoptionListing }) {
  const navigate = useNavigate();
  const { remove, mark_adopted, close } = use_listing_mutations();
  const primary = listing.photos.find((p) => p.is_primary) ?? listing.photos[0];
  const age = age_label(listing);

  async function del() {
    if (!window.confirm('Delete this listing? This cannot be undone.')) return;
    await remove.mutateAsync(listing.id);
  }

  return (
    <li className="flex flex-col gap-3 p-4" data-testid={`my-listing-${listing.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-input bg-primary/10 text-primary">
          {primary ? <AuthImage path={`${primary.file_path}?variant=thumb`} alt="" className="h-full w-full object-cover" /> : <Heart className="h-6 w-6" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{SPECIES_LABEL[listing.species]}{listing.breed ? ` · ${listing.breed}` : ''}</span>
            <StatusPill tone={STATUS_TONE[listing.status]}>{listing.status.toLowerCase()}</StatusPill>
          </div>
          <p className="text-sm text-muted-foreground">{[age, listing.gender !== 'UNKNOWN' ? listing.gender.toLowerCase() : null, listing.city].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {(listing.status === 'DRAFT' || listing.status === 'PUBLISHED') && (
            <Button size="sm" variant="ghost" onClick={() => navigate(`/app/adopt/edit/${listing.id}`)} data-testid={`listing-edit-${listing.id}`}>
              <Pencil className="h-4 w-4" /> {listing.status === 'DRAFT' ? 'Continue' : 'Edit'}
            </Button>
          )}
          {listing.status === 'PUBLISHED' && (
            <>
              <Button size="sm" variant="outline" disabled={mark_adopted.isPending} onClick={() => mark_adopted.mutate(listing.id)} data-testid={`listing-adopted-${listing.id}`}>
                Mark adopted
              </Button>
              <Button size="sm" variant="ghost" disabled={close.isPending} onClick={() => close.mutate(listing.id)} data-testid={`listing-close-${listing.id}`}>
                Close
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={del} aria-label="Delete" data-testid={`listing-delete-${listing.id}`}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>
      {listing.status === 'PUBLISHED' && <InterestedAdopters listing_id={listing.id} />}
    </li>
  );
}

export function MyListingsPage() {
  const listings_q = use_my_listings();
  const listings = listings_q.data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader title="Adopt a pet" description="Give a rescued or extra pet a loving home." />
      <AdoptTabs />

      {listings_q.isLoading ? (
        <Card><div className="flex items-center justify-center p-10 text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div></Card>
      ) : listings_q.error ? (
        <ErrorState data-testid="my-listings-error" title="Couldn't load your listings" description={form_error_message(listings_q.error, 'Please try again.')} />
      ) : listings.length === 0 ? (
        <EmptyState data-testid="my-listings-empty" icon={Heart} title="No listings yet" description="Found a stray or have puppies to rehome? Post them here." />
      ) : (
        <Card>
          <ul className="divide-y-2 divide-brand-outline-variant/10" data-testid="my-listings">
            {listings.map((l) => <ListingRow key={l.id} listing={l} />)}
          </ul>
        </Card>
      )}
    </div>
  );
}
